import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  TablePagination,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  AlertTitle,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  PlaylistAdd as BulkAddIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import {
  superAdminService,
  Permission,
  CreatePermissionRequest,
  UpdatePermissionRequest,
  ListPermissionsRequest,
} from "../../../services/super-admin.service";
import { useAuth } from "../../auth/hooks/useAuth";

interface PermissionFormData {
  permission_code: string;
  section: string;
  module: string;
  action: string;
  description: string;
  is_active: boolean;
}

interface BulkPermissionData {
  section: string;
  module: string;
  action: string;
  description: string;
  status?: "pending" | "loading" | "success" | "error";
  error?: string;
}

interface BulkResult {
  permission_code: string;
  success: boolean;
  error?: string;
}

interface DeleteConfirmationState {
  open: boolean;
  permission: Permission | null;
}

const initialFormData: PermissionFormData = {
  permission_code: "",
  section: "",
  module: "",
  action: "",
  description: "",
  is_active: true,
};

const initialBulkData: BulkPermissionData = {
  section: "",
  module: "",
  action: "",
  description: "",
};

// Available sections (previously modules)
const availableSections = [
  { value: "hr", label: "HR" },
  { value: "pos", label: "POS" },
  { value: "accounting", label: "Accounting" },
  { value: "inventory", label: "Inventory" },
];

// Common actions for hints
const commonActions = [
  "view",
  "create",
  "edit",
  "delete",
  "list",
  "export",
  "import",
  "approve",
  "reject",
  "manage",
];

export const PermissionsManagement: React.FC = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResultsOpen, setBulkResultsOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(
    null
  );
  const [formData, setFormData] = useState<PermissionFormData>(initialFormData);
  const [bulkPermissions, setBulkPermissions] = useState<BulkPermissionData[]>([
    initialBulkData,
  ]);
  const [bulkResults, setBulkResults] = useState<BulkResult[]>([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkResultsMode, setBulkResultsMode] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<DeleteConfirmationState>({
      open: false,
      permission: null,
    });

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [search, setSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(""); // "true", "false", or ""

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const params: ListPermissionsRequest = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
        search: search || undefined,
        module: sectionFilter || undefined, // API still uses 'module' field
        group: moduleFilter || undefined, // API still uses 'group' field
        action: actionFilter || undefined,
        is_active: statusFilter !== "" ? statusFilter === "true" : undefined,
      };

      const response = await superAdminService.listPermissions(params);

      if (response.data.success) {
        // Fix: API returns data.permissions, not data.data.data
        setPermissions(response.data.data.permissions || []);
        // Fix: API returns data.total, not data.metadata.total.value
        setTotalCount(response.data.data.total || 0);
      } else {
        toast.error("Failed to fetch permissions");
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      toast.error("Failed to fetch permissions");
    } finally {
      setLoading(false);
    }
  };

  const generatePermissionCode = (
    section: string,
    module: string,
    action: string
  ) => {
    if (section && module && action) {
      return `${section.toLowerCase()}.${module.toLowerCase()}.${action.toLowerCase()}`;
    }
    return "";
  };

  const handleSubmit = async () => {
    if (
      !formData.permission_code ||
      !formData.section ||
      !formData.module ||
      !formData.action
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      if (editingPermission) {
        // Update existing permission
        const updateData: UpdatePermissionRequest = {
          _id: editingPermission._id!,
          permission_code: formData.permission_code,
          module: formData.section, // API uses 'module' for our 'section'
          group: formData.module, // API uses 'group' for our 'module'
          action: formData.action,
          description: formData.description || undefined,
          is_active: formData.is_active,
        };

        const response = await superAdminService.updatePermission(updateData);

        if (response.data.success) {
          toast.success("Permission updated successfully");
          handleCloseForm();
          fetchPermissions();
        } else {
          toast.error("Failed to update permission");
        }
      } else {
        // Create new permission
        const createData: CreatePermissionRequest = {
          permission_code: formData.permission_code,
          module: formData.section, // API uses 'module' for our 'section'
          group: formData.module, // API uses 'group' for our 'module'
          action: formData.action,
          description: formData.description || undefined,
        };

        const response = await superAdminService.createPermission(createData);

        if (response.data.success) {
          toast.success("Permission created successfully");
          handleCloseForm();
          fetchPermissions();
        } else {
          toast.error(response.data.message || "Failed to create permission");
        }
      }
    } catch (error) {
      console.error("Error saving permission:", error);
      toast.error(
        `Failed to ${editingPermission ? "update" : "create"} permission`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSubmit = async () => {
    const validPermissions = bulkPermissions.filter(
      (p) => p.section && p.module && p.action
    );

    if (validPermissions.length === 0) {
      toast.error("Please add at least one valid permission");
      return;
    }

    setBulkProcessing(true);
    setBulkResultsMode(true); // Switch to results mode

    // Reset all statuses to pending
    setBulkPermissions((prev) =>
      prev.map((p) => ({
        ...p,
        status: p.section && p.module && p.action ? "pending" : undefined,
      }))
    );

    const results: BulkResult[] = [];

    // Process each permission one by one
    for (let i = 0; i < validPermissions.length; i++) {
      const permission = validPermissions[i];
      const permissionIndex = bulkPermissions.findIndex(
        (p) =>
          p.section === permission.section &&
          p.module === permission.module &&
          p.action === permission.action
      );

      const permissionCode = generatePermissionCode(
        permission.section,
        permission.module,
        permission.action
      );

      // Set this permission to loading
      setBulkPermissions((prev) =>
        prev.map((p, idx) =>
          idx === permissionIndex ? { ...p, status: "loading" } : p
        )
      );

      try {
        const createData: CreatePermissionRequest = {
          permission_code: permissionCode,
          module: permission.section, // API uses 'module' for our 'section'
          group: permission.module, // API uses 'group' for our 'module'
          action: permission.action,
          description: permission.description || undefined,
        };

        const response = await superAdminService.createPermission(createData);

        const success = response.data.success;
        const error = success
          ? undefined
          : response.data.message || "Failed to create permission";

        // Update this permission's status
        setBulkPermissions((prev) =>
          prev.map((p, idx) =>
            idx === permissionIndex
              ? {
                  ...p,
                  status: success ? "success" : "error",
                  error: error,
                }
              : p
          )
        );

        results.push({
          permission_code: permissionCode,
          success: success,
          error: error,
        });
      } catch (error) {
        console.error("Bulk create error:", error);

        // Update this permission's status to error
        setBulkPermissions((prev) =>
          prev.map((p, idx) =>
            idx === permissionIndex
              ? {
                  ...p,
                  status: "error",
                  error: "Network error or server error",
                }
              : p
          )
        );

        results.push({
          permission_code: permissionCode,
          success: false,
          error: "Network error or server error",
        });
      }
    }

    setBulkResults(results);
    setBulkProcessing(false);

    // Refresh the permissions list
    fetchPermissions();
  };

  const handleEdit = (permission: Permission) => {
    setEditingPermission(permission);
    setFormData({
      permission_code: permission.permission_code,
      section: permission.module, // API 'module' is our 'section'
      module: permission.group, // API 'group' is our 'module'
      action: permission.action,
      description: permission.description || "",
      is_active: permission.is_active,
    });
    setFormOpen(true);
  };

  const handleDelete = async (permission: Permission) => {
    setDeleteConfirmation({
      open: true,
      permission: permission,
    });
  };

  const handleConfirmDelete = async () => {
    const permission = deleteConfirmation.permission;
    if (!permission) return;

    try {
      const response = await superAdminService.deletePermission(
        permission._id!
      );

      if (response.data.success) {
        toast.success("Permission deleted successfully");
        fetchPermissions();
      } else {
        toast.error("Failed to delete permission");
      }
    } catch (error) {
      console.error("Error deleting permission:", error);
      toast.error("Failed to delete permission");
    } finally {
      setDeleteConfirmation({
        open: false,
        permission: null,
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({
      open: false,
      permission: null,
    });
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingPermission(null);
    setFormData(initialFormData);
  };

  const handleCloseBulk = () => {
    setBulkOpen(false);
    setBulkPermissions([{ ...initialBulkData }]);
    setBulkResultsMode(false);
  };

  const handleCloseBulkResults = () => {
    setBulkResultsOpen(false);
    setBulkResults([]);
    setBulkOpen(false);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setSearch("");
    setSectionFilter("");
    setModuleFilter("");
    setActionFilter("");
    setStatusFilter("");
    setPage(0);
  };

  const addBulkPermission = () => {
    setBulkPermissions([...bulkPermissions, { ...initialBulkData }]);
  };

  const removeBulkPermission = (index: number) => {
    if (bulkPermissions.length > 1) {
      setBulkPermissions(bulkPermissions.filter((_, i) => i !== index));
    }
  };

  const updateBulkPermission = (
    index: number,
    field: keyof BulkPermissionData,
    value: string
  ) => {
    const updated = [...bulkPermissions];
    updated[index] = { ...updated[index], [field]: value };
    setBulkPermissions(updated);
  };

  // Auto-generate permission code when section, module, or action changes
  useEffect(() => {
    const autoCode = generatePermissionCode(
      formData.section,
      formData.module,
      formData.action
    );
    if (autoCode && autoCode !== formData.permission_code) {
      setFormData((prev) => ({ ...prev, permission_code: autoCode }));
    }
  }, [formData.section, formData.module, formData.action]);

  useEffect(() => {
    fetchPermissions();
  }, [
    page,
    rowsPerPage,
    search,
    sectionFilter,
    moduleFilter,
    actionFilter,
    statusFilter,
  ]);

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case "loading":
        return <CircularProgress size={16} />;
      case "success":
        return <CheckCircleIcon color="success" fontSize="small" />;
      case "error":
        return <ErrorIcon color="error" fontSize="small" />;
      case "pending":
        return <CircularProgress size={16} sx={{ opacity: 0.5 }} />;
      default:
        return null;
    }
  };

  // Show loading if user is not yet loaded
  if (!user) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <SecurityIcon sx={{ color: "primary.main", fontSize: 32 }} />
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              Permissions Management
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Manage system permissions and access controls
            </Typography>
          </Box>
        </Stack>
      </motion.div>

      {/* Usage Hints */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.05 }}
      >
        <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            How to use permissions:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Section:</strong> Choose the main area (HR, POS,
            Accounting, Inventory)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Module:</strong> Specify the specific module/feature
            (e.g., employees, products, reports)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Action:</strong> Define what action is allowed (e.g.,
            view, create, edit, delete)
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Example: <code>hr.employees.view</code> allows viewing employee
            records in HR section
          </Typography>
        </Alert>
      </motion.div>

      {/* Filters and Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Stack spacing={3}>
            {/* Search and Actions Row */}
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                placeholder="Search permissions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setFormOpen(true)}
                sx={{ minWidth: 140 }}
              >
                Add Permission
              </Button>
              <Button
                variant="outlined"
                startIcon={<BulkAddIcon />}
                onClick={() => setBulkOpen(true)}
                sx={{ minWidth: 140 }}
              >
                Bulk Add
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchPermissions}
                disabled={loading}
              >
                Refresh
              </Button>
            </Stack>

            {/* Filters Row */}
            <Stack direction="row" spacing={2} alignItems="center">
              <FilterIcon sx={{ color: "text.secondary" }} />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Section</InputLabel>
                <Select
                  value={sectionFilter}
                  onChange={(e) => setSectionFilter(e.target.value)}
                  label="Section"
                >
                  <MenuItem value="">All Sections</MenuItem>
                  {availableSections.map((section) => (
                    <MenuItem key={section.value} value={section.value}>
                      {section.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Module"
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
                sx={{ minWidth: 120 }}
              />

              <TextField
                size="small"
                placeholder="Action"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                sx={{ minWidth: 120 }}
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as string)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>

              <Button size="small" onClick={clearFilters}>
                Clear Filters
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </motion.div>

      {/* Permissions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>Permission Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : permissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      No permissions found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                permissions.map((permission) => (
                  <TableRow
                    key={permission._id}
                    sx={{
                      "&:hover": { bgcolor: "action.hover" },
                      opacity: permission.is_active ? 1 : 0.6,
                    }}
                  >
                    <TableCell
                      sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                    >
                      {permission.permission_code}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={permission.module}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={permission.group}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={permission.action}
                        size="small"
                        variant="outlined"
                        color="info"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap>
                        {permission.description || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={permission.is_active ? "Active" : "Inactive"}
                        size="small"
                        color={permission.is_active ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {permission.created_by?.name || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit Permission">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(permission)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Permission">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(permission)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </TableContainer>
      </motion.div>

      {/* Add/Edit Permission Dialog */}
      <Dialog
        open={formOpen}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingPermission ? "Edit Permission" : "Add New Permission"}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3}>
            {/* Permission Code Display - Read Only */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Permission Code (Auto-generated)
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "grey.50",
                  fontFamily: "monospace",
                  fontSize: "1rem",
                  minHeight: 40,
                  display: "flex",
                  alignItems: "center",
                  color: formData.permission_code
                    ? "text.primary"
                    : "text.disabled",
                }}
              >
                {formData.permission_code ||
                  "Complete the form to generate permission code"}
              </Paper>
            </Box>

            {/* Section Selection */}
            <FormControl fullWidth required>
              <InputLabel>Section</InputLabel>
              <Select
                value={formData.section}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, section: e.target.value }))
                }
                label="Section"
              >
                {availableSections.map((section) => (
                  <MenuItem key={section.value} value={section.value}>
                    {section.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Module Text Field */}
            <TextField
              label="Module"
              value={formData.module}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, module: e.target.value }))
              }
              required
              fullWidth
              placeholder="e.g., employees, products, reports"
              helperText="Specify the module or feature name"
            />

            {/* Action Text Field */}
            <TextField
              label="Action"
              value={formData.action}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, action: e.target.value }))
              }
              required
              fullWidth
              placeholder="e.g., view, create, edit, delete"
              helperText={`Common actions: ${commonActions.join(", ")}`}
            />

            {/* Description */}
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              multiline
              rows={3}
              fullWidth
              placeholder="Describe what this permission allows..."
            />

            {/* Active Status - only show when editing */}
            {editingPermission && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                }
                label="Active"
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleCloseForm} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              saving ||
              !formData.permission_code ||
              !formData.section ||
              !formData.module ||
              !formData.action
            }
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? "Saving..." : editingPermission ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Add Dialog */}
      <Dialog
        open={bulkOpen}
        onClose={handleCloseBulk}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            fontWeight: 600,
          }}
        >
          {bulkResultsMode ? "Creating Permissions" : "Bulk Add Permissions"}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {!bulkResultsMode ? (
            // Form Mode
            <>
              <Alert
                severity="info"
                sx={{ mb: 3, borderRadius: 1 }}
                icon={<InfoIcon />}
              >
                <AlertTitle>Permission Structure</AlertTitle>
                Permissions are structured as:{" "}
                <strong>section.module.action</strong>
                <br />
                <strong>Common actions:</strong> view, create, edit, delete,
                manage, assign
              </Alert>

              <Stack spacing={3}>
                {bulkPermissions.map((permission, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Card
                      elevation={2}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": {
                          boxShadow: 3,
                          borderColor: "primary.main",
                        },
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth>
                            <InputLabel>Section</InputLabel>
                            <Select
                              value={permission.section}
                              onChange={(e) =>
                                updateBulkPermission(
                                  index,
                                  "section",
                                  e.target.value
                                )
                              }
                              label="Section"
                            >
                              <MenuItem value="HR">HR</MenuItem>
                              <MenuItem value="POS">POS</MenuItem>
                              <MenuItem value="Accounting">Accounting</MenuItem>
                              <MenuItem value="Inventory">Inventory</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Module"
                            value={permission.module}
                            onChange={(e) =>
                              updateBulkPermission(
                                index,
                                "module",
                                e.target.value
                              )
                            }
                            placeholder="e.g., employees, shifts"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Action"
                            value={permission.action}
                            onChange={(e) =>
                              updateBulkPermission(
                                index,
                                "action",
                                e.target.value
                              )
                            }
                            placeholder="e.g., view, create, edit"
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              height: "100%",
                            }}
                          >
                            <IconButton
                              onClick={() => removeBulkPermission(index)}
                              disabled={bulkPermissions.length <= 1}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Description"
                            value={permission.description}
                            onChange={(e) =>
                              updateBulkPermission(
                                index,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Brief description of this permission"
                            multiline
                            rows={2}
                            size="small"
                          />
                        </Grid>
                        {permission.section &&
                          permission.module &&
                          permission.action && (
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Permission Code:
                                </Typography>
                                <Chip
                                  label={generatePermissionCode(
                                    permission.section,
                                    permission.module,
                                    permission.action
                                  )}
                                  size="small"
                                  sx={{
                                    backgroundColor: "primary.main",
                                    color: "white",
                                    fontFamily: "monospace",
                                    fontWeight: "bold",
                                  }}
                                />
                              </Box>
                            </Grid>
                          )}
                      </Grid>
                    </Card>
                  </motion.div>
                ))}

                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={addBulkPermission}
                    startIcon={<AddIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Add Another Permission
                  </Button>
                </Box>
              </Stack>
            </>
          ) : (
            // Results Mode - Minimal view with status indicators
            <Stack spacing={2}>
              <Alert
                severity="info"
                sx={{ mb: 2, borderRadius: 1 }}
                icon={<InfoIcon />}
              >
                <AlertTitle>Creating Permissions</AlertTitle>
                Processing{" "}
                {
                  bulkPermissions.filter(
                    (p) => p.section && p.module && p.action
                  ).length
                }{" "}
                permissions...
              </Alert>

              {bulkPermissions
                .filter((p) => p.section && p.module && p.action)
                .map((permission, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Card
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor:
                          permission.status === "success"
                            ? "rgba(76, 175, 80, 0.05)"
                            : permission.status === "error"
                            ? "rgba(244, 67, 54, 0.05)"
                            : "transparent",
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            {generatePermissionCode(
                              permission.section,
                              permission.module,
                              permission.action
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {permission.description || "No description"}
                          </Typography>
                        </Box>

                        {/* Status Indicator */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {permission.status === "pending" && (
                            <Tooltip title="Waiting to process">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CircularProgress size={16} />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Pending
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}

                          {permission.status === "loading" && (
                            <Tooltip title="Processing...">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CircularProgress size={16} color="primary" />
                                <Typography variant="body2" color="primary">
                                  Creating...
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}

                          {permission.status === "success" && (
                            <Tooltip title="Successfully created">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CheckCircleIcon
                                  sx={{ color: "success.main", fontSize: 18 }}
                                />
                                <Typography
                                  variant="body2"
                                  color="success.main"
                                >
                                  Success
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}

                          {permission.status === "error" && (
                            <Tooltip
                              title={permission.error || "Failed to create"}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <ErrorIcon
                                  sx={{ color: "error.main", fontSize: 18 }}
                                />
                                <Typography variant="body2" color="error.main">
                                  Failed
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </Stack>

                      {/* Error message */}
                      {permission.status === "error" && permission.error && (
                        <Alert
                          severity="error"
                          sx={{ mt: 1, py: 0.5 }}
                          icon={false}
                        >
                          <Typography variant="body2">
                            {permission.error}
                          </Typography>
                        </Alert>
                      )}
                    </Card>
                  </motion.div>
                ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          {!bulkResultsMode ? (
            // Form Mode Actions
            <>
              <Button onClick={handleCloseBulk} sx={{ borderRadius: 2 }}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkSubmit}
                variant="contained"
                disabled={
                  bulkPermissions.filter(
                    (p) => p.section && p.module && p.action
                  ).length === 0
                }
                sx={{ borderRadius: 2 }}
              >
                Create{" "}
                {
                  bulkPermissions.filter(
                    (p) => p.section && p.module && p.action
                  ).length
                }{" "}
                Permissions
              </Button>
            </>
          ) : (
            // Results Mode Actions
            <>
              <Button
                onClick={handleCloseBulk}
                variant="contained"
                disabled={bulkProcessing}
                sx={{ borderRadius: 2 }}
              >
                {bulkProcessing ? "Processing..." : "Close"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk Results Dialog */}
      <Dialog
        open={bulkResultsOpen}
        onClose={handleCloseBulkResults}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Bulk Add Results
            </Typography>
            <IconButton onClick={handleCloseBulkResults} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Alert
            severity={
              bulkResults.every((r) => r.success) ? "success" : "warning"
            }
            sx={{ mb: 3 }}
          >
            <Typography variant="body2">
              {bulkResults.filter((r) => r.success).length} of{" "}
              {bulkResults.length} permissions created successfully
            </Typography>
          </Alert>

          <List>
            {bulkResults.map((result, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemIcon>
                  {result.success ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <ErrorIcon color="error" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                    >
                      {result.permission_code}
                    </Typography>
                  }
                  secondary={
                    result.success ? "Created successfully" : result.error
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleCloseBulkResults} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.open}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ErrorIcon color="error" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Delete Permission
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this permission?
          </Typography>

          {deleteConfirmation.permission && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderColor: "error.main",
                borderWidth: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "error.main",
                }}
              >
                {deleteConfirmation.permission.permission_code}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 1, color: "text.secondary" }}
              >
                {deleteConfirmation.permission.description || "No description"}
              </Typography>
            </Paper>
          )}

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This action cannot be undone. The permission will be permanently
              deleted from the system.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleCancelDelete} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Permission
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
